import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExclamationTriangle, faCheckCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

/**
 * ConfirmModal - Modal de confirmation réutilisable
 * 
 * @param {boolean} isOpen - État d'ouverture de la modal
 * @param {function} onClose - Callback pour fermer la modal
 * @param {function} onConfirm - Callback de confirmation
 * @param {string} title - Titre de la modal
 * @param {string|React.ReactNode} message - Message ou contenu de la modal
 * @param {string} confirmText - Texte du bouton de confirmation
 * @param {string} cancelText - Texte du bouton d'annulation
 * @param {string} variant - Type de modal: 'danger' | 'warning' | 'info' | 'success'
 * @param {boolean} loading - État de chargement
 */
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirmer l\'action',
  message = 'Êtes-vous sûr de vouloir continuer ?',
  confirmText = 'Confirmer',
  cancelText = 'Annuler',
  variant = 'danger',
  loading = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAnimating(true);
      requestAnimationFrame(() => {
        setVisible(true);
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setAnimating(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen && !animating) return null;

  const variantConfig = {
    danger: {
      icon: faExclamationTriangle,
      iconBg: 'bg-red-100 dark:bg-red-900/30',
      iconColor: 'text-red-600 dark:text-red-400',
      confirmBg: 'bg-red-600 hover:bg-red-700',
      confirmShadow: 'shadow-red-500/20',
    },
    warning: {
      icon: faExclamationTriangle,
      iconBg: 'bg-amber-100 dark:bg-amber-900/30',
      iconColor: 'text-amber-600 dark:text-amber-400',
      confirmBg: 'bg-amber-600 hover:bg-amber-700',
      confirmShadow: 'shadow-amber-500/20',
    },
    info: {
      icon: faInfoCircle,
      iconBg: 'bg-blue-100 dark:bg-blue-900/30',
      iconColor: 'text-blue-600 dark:text-blue-400',
      confirmBg: 'bg-blue-600 hover:bg-blue-700',
      confirmShadow: 'shadow-blue-500/20',
    },
    success: {
      icon: faCheckCircle,
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
      iconColor: 'text-emerald-600 dark:text-emerald-400',
      confirmBg: 'bg-emerald-600 hover:bg-emerald-700',
      confirmShadow: 'shadow-emerald-500/20',
    },
  };

  const config = variantConfig[variant] || variantConfig.danger;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (!loading) {
      onConfirm();
    }
  };

  return (
    <div
      className={`
        fixed inset-0 z-50 flex items-center justify-center p-4 
        transition-all duration-200 ease-out
        ${visible ? 'bg-black/50 backdrop-blur-sm' : 'bg-black/0'}
      `}
      onClick={handleBackdropClick}
    >
      <div 
        className={`
          relative w-full max-w-md bg-white dark:bg-gray-900 rounded-lg shadow-2xl 
          border border-slate-200 dark:border-gray-800 
          transition-all duration-200 ease-out
          ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
        `}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={loading}
          className="absolute top-3 right-3 p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faTimes} className="text-sm" />
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Icon */}
          <div className={`w-12 h-12 rounded-lg ${config.iconBg} flex items-center justify-center mb-4`}>
            <FontAwesomeIcon icon={config.icon} className={`text-xl ${config.iconColor}`} />
          </div>

          {/* Title */}
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
            {title}
          </h3>

          {/* Message */}
          <div className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            {typeof message === 'string' ? <p>{message}</p> : message}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 p-4 pt-0">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-300 font-medium text-sm hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className={`flex-1 px-4 py-2.5 rounded-lg ${config.confirmBg} text-white font-medium text-sm shadow-lg ${config.confirmShadow} transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Traitement...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
